import { getNames } from 'country-list';
import { camelize, camelizeKeys } from 'humps';
import React, { createContext, ReactNode, useCallback, useState } from 'react';

import { isFileSizeValid } from '@/features/common';
import { isEmail } from '@/utils/emails';

import {
  IBulkCandidateImport,
  ICandidateImportDataError,
  ICandidateImportError,
  ICSVHeader,
} from '../../types/bulk-candidate-import';

const GITHUB_URL_RE = /(http(s)?:\/\/)?([\w]+\.)?github\.com\/[a-zA-Z0-9._-]+$/;
const LINKEDIN_URL_RE =
  /(http(s)?:\/\/)?([\w]+\.)?linkedin\.com\/in\/[A-z0-9_-]+\/?/;

interface IBulkCandidateImportModalContextProps {
  readCSV: (file?: File) => void;
  clearContext: () => void;
  headerData: string[];
  candidateImportData: IBulkCandidateImport[];
  candidateImportError: ICandidateImportError | null;
}

export const BulkCandidateImportModalContext =
  createContext<IBulkCandidateImportModalContextProps>({
    readCSV: () => {},
    clearContext: () => {},
    headerData: [],
    candidateImportData: [],
    candidateImportError: null,
  });

export function BulkCandidateImportModalContextProvider({ children }) {
  const [headerData, setHeaderData] = useState<string[]>([]);
  const [candidateImportData, setCandidateImportData] = useState<
    IBulkCandidateImport[]
  >([]);
  const [candidateImportError, setCandidateImportError] =
    useState<ICandidateImportError | null>(null);

  const clearContext = useCallback(() => {
    setCandidateImportError(null);
    setCandidateImportData([]);
    setHeaderData([]);
  }, []);

  const readCSV = (file?: File) => {
    setCandidateImportError(null);
    setCandidateImportData([]);
    setHeaderData([]);

    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setCandidateImportError({ type: 'unsupportedFileError' });
      return;
    }
    if (!isFileSizeValid(file, 2)) {
      // this is jsut an extra check to not try to parse a huge csv
      setCandidateImportError({ type: 'fileSizeError' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const raw = e.target?.result as string;
      if (!raw) return;

      const [headerLine, ...rows] = raw.trim().split('\n');
      if (rows.length > 250) {
        setCandidateImportError({ type: 'tooManyRowsError' });
      }
      const headers = headerLine.split(',').map((h) => h.trim());
      const missingHeaders = validateCsvHeader(headers);
      if (missingHeaders.length > 0) {
        setCandidateImportError({
          type: 'missingHeaderError',
          payload: missingHeaders,
        });
        return;
      }

      setHeaderData(headers);

      const parsed: Record<string, string>[] = [];
      const dataErrors: ICandidateImportDataError[] = [];

      rows.forEach((line, rowIndex) => {
        const values = line.split(',').map((v) => v.trim());
        const entry: Record<string, string> = {};

        headers.forEach((header, i) => {
          if (i < values.length) {
            const value = values[i];
            const error = validateCsvData(header, rowIndex, value);
            if (error) {
              dataErrors.push(error);
            } else {
              entry[header] = value;
            }
          }
        });
        parsed.push(entry);
      });
      if (dataErrors.length > 0) {
        setCandidateImportError({
          type: 'invalidDataError',
          payload: dataErrors,
        });
      }
      setCandidateImportData(camelizeKeys(parsed) as any[]);
    };

    reader.readAsText(file);
  };

  return (
    <BulkCandidateImportModalContext.Provider
      value={{
        readCSV,
        clearContext,
        headerData,
        candidateImportData,
        candidateImportError,
      }}
    >
      {children}
    </BulkCandidateImportModalContext.Provider>
  );
}

const validateCsvHeader = (headers: string[]) => {
  const camelizedHeaders = headers.map((h) => camelize(h) as ICSVHeader);
  let missingHeaders: string[] = [];
  if (!camelizedHeaders.includes('firstName')) {
    missingHeaders.push('First Name');
  }
  if (!camelizedHeaders.includes('lastName')) {
    missingHeaders.push('Last Name');
  }
  if (!camelizedHeaders.includes('email')) {
    missingHeaders.push('Email');
  }
  return missingHeaders;
};

const validateCsvData = (
  header: string,
  rowIndex: number,
  value: string
): ICandidateImportDataError | null => {
  const makeError = (
    message: string | ReactNode
  ): ICandidateImportDataError => ({
    // + 2 for indexing from 0 and adding the header
    row: rowIndex + 2,
    column: header,
    errorMessage: message,
  });

  switch (camelize(header) as ICSVHeader) {
    case 'country':
      return value && !getNames().includes(value)
        ? makeError(
            <>
              See{' '}
              <a
                href="https://support.hire.toggl.com/en/articles/11184315-accepted-countries-for-csv-upload"
                target="_blank"
                rel="noreferrer"
              >
                accepted country values
              </a>
            </>
          )
        : null;

    case 'email':
      return !value
        ? makeError('Email is missing')
        : !isEmail(value)
          ? makeError('Email address not valid')
          : null;

    case 'firstName':
      return !value ? makeError('First name is missing') : null;

    case 'lastName':
      return !value ? makeError('Last name is missing') : null;

    case 'gitHubProfile':
      return value && !GITHUB_URL_RE.test(value)
        ? makeError('Github profile URL not valid')
        : null;

    case 'linkedInProfile':
      return value && !LINKEDIN_URL_RE.test(value)
        ? makeError('LinkedIn profile URL not valid')
        : null;

    default:
      return null;
  }
};
